import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { fmtPrice } from "../lib/format";
import { COUNTRIES, regionForCountry } from "../lib/regions";
import { COUNTRY_CODES } from "../lib/countryCodes";
import api, { resolveImg } from "../lib/api";
import { toast } from "sonner";
import { logger } from "../lib/logger";
import { CheckoutAction, shippingLabel } from "../components/checkout/CheckoutAction";
import { calcShippingAed, zonesOf, courierOf } from "../lib/shipping";

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A857D]">{label}</label>
    <div className="mt-2">{children}</div>
  </div>
);

const inputCls = "w-full bg-transparent border border-[#262421] px-4 py-3 outline-none focus:border-[#C49A6C] transition-colors text-[#F2EBE1] text-sm";

export default function Checkout() {
  const { t, lang, currency, money, convert } = useLang();
  const { items, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({ shipping_fees: {}, free_shipping_threshold: 0 });
  const [form, setForm] = useState({
    full_name: user?.name || "", email: user?.email || "", phone: "", dial_code: "+971", country: "United Arab Emirates",
    emirate: "", zone: "", region: "UAE", city: "", address: "", building: "", apartment: "", notes: "",
  });
  const [discountCode, setDiscountCode] = useState("");
  const [discount, setDiscount] = useState(null);
  const [payment, setPayment] = useState("card");
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const leaving = useRef(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch((e) => logger.error("settings load failed", e));
  }, []);
  const cartEmpty = items.length === 0;
  useEffect(() => { if (cartEmpty && !leaving.current) navigate("/shop"); }, [cartEmpty, navigate]);

  const isDomestic = form.country === "United Arab Emirates";
  const region = regionForCountry(form.country); // "UAE" or "GCC"

  // Shipping from the admin rate card: UAE per area, GCC land/air by weight.
  const shippingCourier = courierOf(settings, form.country);
  const [shipMethod, setShipMethod] = useState("land");
  const qtyTotal = items.reduce((n, i) => n + i.qty, 0);
  const shipFor = (method) => calcShippingAed(settings, { country: form.country, zone: form.zone, method, qty: qtyTotal, subtotalAed: subtotal });
  const fee = shipFor(shipMethod);
  const shippingFeeAed = fee ?? 0;
  const shippingKnown = fee != null;
  const isEstimated = false;
  const shippingLoading = !settings.shipping_rates;

  // Amounts are computed in AED base, then displayed in the selected currency
  const discountAmountAed = (() => {
    if (!discount) return 0;
    if (discount.type === "percent") return Math.round(subtotal * discount.value / 100 * 100) / 100;
    return Math.min(discount.value, subtotal);
  })();

  const totalAed = Math.max(0, subtotal + shippingFeeAed - discountAmountAed);

  const salesOpen = (() => {
    const s = settings || {};
    if (s.sales_open === false) return false;
    const today = new Date().toISOString().slice(0, 10);
    if (s.sales_start && today < s.sales_start) return false;
    if (s.sales_end && today > s.sales_end) return false;
    return true;
  })();

  const onCountry = (name) => {
    setForm({ ...form, country: name, region: regionForCountry(name) });
  };

  const applyDiscount = async () => {
    if (!discountCode) return;
    try {
      const { data } = await api.post("/discounts/validate", { code: discountCode });
      setDiscount(data);
      toast.success("Discount applied");
    } catch {
      setDiscount(null);
      toast.error("Invalid or expired code");
    }
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!salesOpen) {
      toast.error(t("sales_closed_msg"));
      return;
    }
    if (!agreed) {
      toast.error(t("must_agree"));
      return;
    }
    if (isDomestic && !form.zone) {
      toast.error(t("choose_area"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        customer: { ...form, region, phone: form.phone ? `${form.dial_code} ${form.phone}` : form.phone },
        shipping_method: isDomestic ? "local" : shipMethod,
        currency,
        lang,
        discount_code: discount ? discountCode : "",
        payment_method: payment,
        terms_accepted: agreed,
      });
      leaving.current = true;
      // Start Ziina payment
      const { data: pay } = await api.post("/payments/checkout", {
        order_number: data.order_number,
        origin_url: window.location.origin,
      });
      window.location.href = pay.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
      setLoading(false);
    }
  };

  const intlDisabled = false;

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-16 pb-28 md:py-24">
      <h1 className="serif text-4xl md:text-5xl mb-12">{t("checkout_title")}</h1>
      <form onSubmit={placeOrder} className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-10">
          <div>
            <h2 className="text-xs uppercase tracking-[0.2em] text-[#C49A6C] mb-6">{t("contact_details")}</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label={t("full_name")}><input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="checkout-name" className={inputCls} /></Field>
              <Field label={t("email")}><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="checkout-email" className={inputCls} /></Field>
              <Field label={t("phone")}>
                <div className="flex gap-2">
                  <select value={form.dial_code} onChange={(e) => setForm({ ...form, dial_code: e.target.value })} data-testid="checkout-dial-code" dir="ltr" className={`${inputCls} max-w-[130px] shrink-0`}>
                    {COUNTRY_CODES.map(([flag, name, code], i) => (
                      <option key={`${code}-${i}`} value={code} className="bg-[#141311]">{flag} {code}</option>
                    ))}
                  </select>
                  <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="checkout-phone" dir="ltr" className={inputCls} placeholder="50 123 4567" />
                </div>
              </Field>
              <Field label={t("country")}>
                <select value={form.country} onChange={(e) => onCountry(e.target.value)} data-testid="checkout-country" className={inputCls}>
                  {COUNTRIES.map((c) => <option key={c.name} value={c.name} className="bg-[#0F0F0F]">{lang === "ar" ? c.ar : c.name}</option>)}
                </select>
              </Field>
            </div>
          </div>

          <div>
            <h2 className="text-xs uppercase tracking-[0.2em] text-[#C49A6C] mb-6">{t("shipping_address")}</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {isDomestic ? (
                <Field label={t("area")}>
                  <select value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} data-testid="checkout-emirate" className={inputCls}>
                    <option value="" className="bg-[#0F0F0F]">{t("choose_area")}</option>
                    {zonesOf(settings).map((z) => <option key={z.key} value={z.key} className="bg-[#0F0F0F]">{lang === "ar" ? z.ar : z.en} · {money(z.price)}</option>)}
                  </select>
                </Field>
              ) : (
                <Field label={t("shipping_method")}>
                  <div className="grid grid-cols-2 gap-2" data-testid="checkout-ship-method">
                    {["land", "air"].map((m) => (
                      <button type="button" key={m} onClick={() => setShipMethod(m)} data-testid={`checkout-ship-${m}`}
                        className={`border px-3 py-3 text-start transition-colors ${shipMethod === m ? "border-[#C49A6C] text-[#F2EBE1]" : "border-[#262421] text-[#8A857D] hover:border-[#3A362F]"}`}>
                        <span className="block text-xs uppercase tracking-[0.15em]">{t(`ship_${m}`)}</span>
                        <span className="block text-sm text-[#C49A6C] mt-1">{shipFor(m) == null ? "…" : money(shipFor(m))}</span>
                      </button>
                    ))}
                  </div>
                </Field>
              )}
              <Field label={t("city")}><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="checkout-city" className={inputCls} /></Field>
              <div className="sm:col-span-2"><Field label={t("address")}><input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="checkout-address" className={inputCls} /></Field></div>
              <Field label={t("building")}><input value={form.building} onChange={(e) => setForm({ ...form, building: e.target.value })} data-testid="checkout-building" className={inputCls} /></Field>
              <Field label={t("apartment")}><input value={form.apartment} onChange={(e) => setForm({ ...form, apartment: e.target.value })} data-testid="checkout-apartment" className={inputCls} /></Field>
              <div className="sm:col-span-2"><Field label={t("delivery_notes")}><textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="checkout-notes" className={inputCls + " resize-none"} /></Field></div>
            </div>
            {!isDomestic && (
              <p className="text-xs text-[#c9b978] leading-relaxed mt-5 border-s-2 border-[#3a3a2a] ps-4">
                {t("gcc_note")}
              </p>
            )}
          </div>

          <div>
            <h2 className="text-xs uppercase tracking-[0.2em] text-[#C49A6C] mb-6">{t("payment_method")}</h2>
            <div className="space-y-3">
              {[["card", "Credit / Debit Card"], ["apple_pay", "Apple Pay"], ["google_pay", "Google Pay"]].map(([val, label]) => (
                <label key={val} className={`flex items-center gap-4 border px-5 py-4 cursor-pointer transition-colors ${payment === val ? "border-[#C49A6C]" : "border-[#262421]"}`} data-testid={`payment-${val}`}>
                  <input type="radio" name="payment" checked={payment === val} onChange={() => setPayment(val)} className="accent-[#C49A6C]" />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-[#6E685F] leading-relaxed mt-5 border-s-2 border-[#262421] ps-4">{t("payment_note")}</p>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-[#0F0F0F] border border-[#262421] p-8 lg:sticky lg:top-24">
            <h2 className="text-xs uppercase tracking-[0.2em] text-[#8A857D] mb-6">{t("order_summary")}</h2>
            <p className="text-[10px] uppercase tracking-widest text-[#c9b978] mb-4">{t("prices_in")} {currency}</p>
            <div className="space-y-4 mb-6">
              {items.map((i) => (
                <div key={i.product_id} className="flex gap-3">
                  <div className="w-14 h-16 bg-[#141311] flex-shrink-0 overflow-hidden">{i.image && <img src={resolveImg(i.image)} alt="" className="w-full h-full object-cover" />}</div>
                  <div className="flex-1 min-w-0">
                    <p className="serif text-base leading-tight">{i.name_en}</p>
                    <p className="text-xs text-[#8A857D] mt-1">× {i.qty}</p>
                  </div>
                  <span className="text-sm text-[#C49A6C] whitespace-nowrap">{money(i.price * i.qty)}</span>
                </div>
              ))}
            </div>

            <div className="flex gap-2 mb-6">
              <input value={discountCode} onChange={(e) => setDiscountCode(e.target.value)} placeholder={t("discount_code")} data-testid="discount-input" className="flex-1 bg-transparent border border-[#262421] px-3 py-2.5 text-sm outline-none focus:border-[#C49A6C]" />
              <button type="button" onClick={applyDiscount} data-testid="discount-apply" className="border border-[#262421] px-4 text-xs uppercase tracking-widest hover:border-[#C49A6C] transition-colors">{t("apply")}</button>
            </div>

            <div className="space-y-3 border-t border-[#262421] pt-5 text-sm">
              <div className="flex justify-between"><span className="text-[#8A857D]">{t("subtotal")}</span><span>{money(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-[#8A857D]">{t("shipping")}{shippingCourier ? ` · ${shippingCourier}` : ""}</span><span>{shippingLabel({ t, money, loading: shippingLoading, known: shippingKnown, feeAed: shippingFeeAed, estimated: isEstimated })}</span></div>
              {discountAmountAed > 0 && <div className="flex justify-between text-[#C49A6C]"><span>{t("discount_code")}</span><span>− {money(discountAmountAed)}</span></div>}
            </div>
            <div className="flex justify-between items-center border-t border-[#262421] mt-5 pt-5">
              <span className="serif text-lg">{t("order_total")}</span>
              <span className="serif text-xl text-[#C49A6C]" data-testid="checkout-total">{money(totalAed)}</span>
            </div>
            {!isDomestic && (
              <p data-testid="customs-notice" className="mt-4 border border-[#3a3a2a] text-[#c9b978] text-xs leading-relaxed p-3">
                {t("customs_notice")} <Link to="/shipping" target="_blank" className="underline">{t("shipping_delivery")}</Link>
              </p>
            )}

            <CheckoutAction t={t} salesOpen={salesOpen} intlDisabled={intlDisabled} agreed={agreed} setAgreed={setAgreed} loading={loading} />
          </div>
        </div>
      </form>
    </div>
  );
}
